// PidUPCModal.jsx — Add/Edit surface for PID UPC on the shared PidModal scaffold.
// One body, two modes (Create/Edit identical by construction — plan decision 2):
//   create — Retailer + Commodity pickers, Count/Weight editor, UPC field; POST /api/pidupc
//   edit   — Retailer/Commodity read-only (immutable on the record), Count/Weight + UPC
//            editable; PUT /api/pidupc/:id { Weight, UPC }; delete via ConfirmDelete
// The live barcode preview (formerly edit-only) now renders in both modes once the UPC
// is a valid 12-digit UPC-A.
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ReactSelect from 'react-select';
import Barcode from 'react-barcode';
import { Plus, Trash2 } from 'lucide-react';
import authAxios from '../utils/authAxios';
import PidModal, {
  PrimaryButton, QuietButton,
  MSection, MSectionTitle,
  DetailGrid, DetailItem,
  FieldInput, FieldError,
} from './PidModal';
import { ConfirmDelete } from '../styles/TableStyles';
import { BarcodeWrapper, customSelectStyles } from '../styles/PackageIndexStyles';
import { digitsOnly, validateUPC12, preventNonDigitsOnKeyDown, handlePasteDigitsOnly } from '../utils/barcodeUtils';
import { buildWeightStorageString, formatWeightForDisplay, parseWeightParts } from '../utils/weightString';

const InputWithSuffix = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const Suffix = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--shadow-a45);
  font-weight: 700;
  pointer-events: none;
  user-select: none;
`;

const WeightRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.6rem;

  &:first-child { margin-top: 0; }
`;

const IconButton = styled.button`
  width: 38px;
  height: 38px;
  flex: none;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:focus-visible { outline: 2px solid var(--green-a45); outline-offset: 1px; }
`;

const AddUnitButton = styled(IconButton)`
  background: var(--green-600);
  color: var(--white);
  &:hover { background: var(--green-700); }
`;

const RemoveUnitButton = styled(IconButton)`
  background: var(--color-danger);
  color: var(--white);
  &:hover { background: var(--color-danger-hover); }
`;

const BarcodeCenter = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
`;

const decimalOk = (v) => /^\d*(?:\.\d*)?$/.test(v);

const PidUPCModal = ({ mode, data, retailers = [], onClose, onSaved, onDeleted }) => {
  const isEdit = mode === 'edit';

  const initial = isEdit ? parseWeightParts(data?.Weight) : { count: '', pounds: '' };
  const [form, setForm] = useState({
    Retailer: isEdit ? (data?.Retailer || '') : '',
    CommodityUniqueID: '',
  });
  const [commodities, setCommodities] = useState([]);
  const [countEnabled, setCountEnabled] = useState(Boolean(initial.count));
  const [poundsEnabled, setPoundsEnabled] = useState(Boolean(initial.pounds));
  const [countValue, setCountValue] = useState(initial.count || '');
  const [poundsValue, setPoundsValue] = useState(initial.pounds || '');
  const [upc, setUpc] = useState(isEdit ? digitsOnly(data?.UPC || '') : '');
  const [upcError, setUpcError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isEdit) return;
    authAxios.get('/api/commodities')
      .then(res => setCommodities(res.data.map(c => ({ value: c.UniqueID, label: c.Commodity }))))
      .catch(err => console.error('[GET /api/commodities]', err));
  }, [isEdit]);

  const handleUpcChange = (value) => {
    const d = digitsOnly(value);
    setUpcError(d.length !== 12 || !validateUPC12(d) ? 'UPC must be 12 digits (valid UPC-A)' : '');
    setUpc(d);
  };

  const upcDigits = digitsOnly(upc || '');
  const upcValid = upcDigits.length === 12 && validateUPC12(upcDigits);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      if (!isEdit) {
        if (!form.Retailer) return alert('Retailer is required');
        if (!form.CommodityUniqueID) return alert('Commodity is required');
      }
      if (!countEnabled && !poundsEnabled) return alert('Count or Weight is required');
      if (countEnabled && !Number.isFinite(Number(countValue))) return alert('Count must be a number');
      if (poundsEnabled && !Number.isFinite(Number(poundsValue))) return alert('Weight must be a number');
      if (!upcValid) return alert('UPC must be 12 digits (valid)');

      const weight = buildWeightStorageString({
        count: countEnabled ? countValue : '',
        pounds: poundsEnabled ? poundsValue : '',
      });
      if (!weight) return alert('Count or Weight is required');

      if (isEdit) {
        await authAxios.put(`/api/pidupc/${data.UniqueID}`, { Weight: weight, UPC: upcDigits });
      } else {
        await authAxios.post('/api/pidupc', {
          Retailer: String(form.Retailer).toUpperCase(),
          CommodityUniqueID: Number(form.CommodityUniqueID),
          Weight: weight,
          UPC: upcDigits,
        });
      }
      onSaved?.();
    } catch (e) {
      if (e?.response?.status === 409) alert('This Retailer + Commodity + Weight already exists');
      else alert(isEdit ? 'Failed to save changes' : 'Failed to save UPC');
      console.error(isEdit ? '[PUT /api/pidupc/:id]' : '[POST /api/pidupc]', e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await authAxios.delete(`/api/pidupc/${data.UniqueID}`);
      onDeleted?.();
    } catch (e) {
      console.error('[DELETE /api/pidupc/:id]', e);
      alert('Failed to delete entry');
    } finally {
      setIsDeleting(false);
    }
  };

  const editName = isEdit ? `${data?.Retailer || ''} ${formatWeightForDisplay(data?.Weight)}`.trim() : '';

  return (
    <PidModal
      title={isEdit ? `Edit PID UPC — ${editName}` : 'New PID UPC'}
      status={isEdit ? data?.Status : 'Pending'}
      onClose={onClose}
      footerLeft={isEdit ? (
        <ConfirmDelete onConfirm={handleDelete} label="Delete" disabled={isSaving || isDeleting} />
      ) : null}
      footerRight={(
        <>
          <QuietButton type="button" onClick={onClose} disabled={isSaving || isDeleting}>Cancel</QuietButton>
          <PrimaryButton type="button" onClick={handleSave} disabled={isSaving || isDeleting || Boolean(upcError)}>
            Save
          </PrimaryButton>
        </>
      )}
    >
      {isEdit ? (
        <MSection>
          <MSectionTitle>Record</MSectionTitle>
          <DetailGrid>
            <DetailItem label="Retailer" value={data?.Retailer} />
            <DetailItem label="Commodity" value={data?.Commodity} />
          </DetailGrid>
        </MSection>
      ) : (
        <MSection>
          <MSectionTitle>Record</MSectionTitle>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px 16px' }}>
            <div>
              <MSectionTitle as="label" style={{ display: 'block', marginBottom: 4 }}>Retailer</MSectionTitle>
              <ReactSelect
                options={retailers.map(r => ({ value: r, label: r }))}
                styles={customSelectStyles}
                value={form.Retailer ? { value: form.Retailer, label: form.Retailer } : null}
                onChange={(sel) => setForm(prev => ({ ...prev, Retailer: sel?.value || '' }))}
                placeholder="Select retailer"
              />
            </div>
            <div>
              <MSectionTitle as="label" style={{ display: 'block', marginBottom: 4 }}>Commodity</MSectionTitle>
              <ReactSelect
                options={commodities}
                styles={customSelectStyles}
                value={commodities.find(c => String(c.value) === String(form.CommodityUniqueID)) || null}
                onChange={(sel) => setForm(prev => ({ ...prev, CommodityUniqueID: sel?.value || '' }))}
                placeholder="Select commodity"
              />
            </div>
          </div>
        </MSection>
      )}

      <MSection>
        <MSectionTitle>Count / Weight</MSectionTitle>

        {!countEnabled && !poundsEnabled ? (
          <ReactSelect
            options={[
              { value: 'Count', label: 'Count' },
              { value: 'Pounds', label: 'Weight' },
            ]}
            styles={customSelectStyles}
            value={null}
            onChange={(sel) => {
              const v = sel?.value || '';
              if (v === 'Count') setCountEnabled(true);
              if (v === 'Pounds') setPoundsEnabled(true);
            }}
            placeholder="Select Count or Weight"
          />
        ) : null}

        {countEnabled ? (
          <WeightRow>
            <InputWithSuffix>
              <FieldInput
                value={countValue}
                onChange={(e) => { if (decimalOk(e.target.value)) setCountValue(e.target.value); }}
                placeholder="Count (e.g. 5)"
                inputMode="decimal"
                style={{ paddingRight: '52px' }}
              />
              <Suffix>CT</Suffix>
            </InputWithSuffix>
            {!poundsEnabled ? (
              <AddUnitButton type="button" title="Add Weight" onClick={() => setPoundsEnabled(true)}>
                <Plus size={18} />
              </AddUnitButton>
            ) : null}
            <RemoveUnitButton
              type="button"
              title="Remove Count"
              onClick={() => { setCountEnabled(false); setCountValue(''); }}
            >
              <Trash2 size={18} />
            </RemoveUnitButton>
          </WeightRow>
        ) : null}

        {poundsEnabled ? (
          <WeightRow>
            <InputWithSuffix>
              <FieldInput
                value={poundsValue}
                onChange={(e) => { if (decimalOk(e.target.value)) setPoundsValue(e.target.value); }}
                placeholder="Weight (lb) (e.g. 4 or 4.5)"
                inputMode="decimal"
                style={{ paddingRight: '52px' }}
              />
              <Suffix>lb</Suffix>
            </InputWithSuffix>
            {!countEnabled ? (
              <AddUnitButton type="button" title="Add Count" onClick={() => setCountEnabled(true)}>
                <Plus size={18} />
              </AddUnitButton>
            ) : null}
            <RemoveUnitButton
              type="button"
              title="Remove Weight"
              onClick={() => { setPoundsEnabled(false); setPoundsValue(''); }}
            >
              <Trash2 size={18} />
            </RemoveUnitButton>
          </WeightRow>
        ) : null}
      </MSection>

      <MSection>
        <MSectionTitle>UPC</MSectionTitle>
        <FieldInput
          name="UPC"
          value={upc}
          onChange={(e) => handleUpcChange(e.target.value)}
          onKeyDown={preventNonDigitsOnKeyDown}
          onPaste={(e) => handlePasteDigitsOnly(e, (d) => handleUpcChange(d))}
          placeholder="12 digits (UPC-A)"
          inputMode="numeric"
          style={{ maxWidth: 320 }}
        />
        {upcError && <FieldError>{upcError}</FieldError>}
        {upcValid && (
          <BarcodeCenter>
            <BarcodeWrapper>
              <Barcode
                value={upcDigits}
                format={'UPC'}
                height={80}
                width={2}
                displayValue
                background="transparent"
                font="OCRB"
              />
            </BarcodeWrapper>
          </BarcodeCenter>
        )}
      </MSection>
    </PidModal>
  );
};

export default PidUPCModal;
